import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Search, X, Plus, RefreshCw, Loader2, Check, Trash2, SlidersHorizontal, ChevronRight, Building2, AlertCircle,
} from 'lucide-react';
import CompanyLogo from './CompanyLogo';
import WatchlistView from './WatchlistView';
import CompanyConfig from './CompanyConfig';
import { apiFetch } from '../api';
import { getRiskLevel } from '../utils/risk';
import { displayHeadline } from '../utils/displayHeadline';
import { relativeTime, formatShortDate, isWatchlisted } from '../utils/format';

const SORTS = [
  ['stories', 'Most stories'],
  ['risk', 'Most high risk'],
  ['latest', 'Latest activity'],
  ['name', 'Name A–Z'],
];
const FILTERS = [
  ['all', 'All companies'],
  ['risk', 'With high-risk stories'],
  ['quiet', 'Quiet this week'],
];

const CARD = 'bg-white border border-[#e1e5ec] rounded-lg';
const BTN = 'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md border border-[#cbd3df] bg-white text-[13px] font-medium text-[#0d1524] whitespace-nowrap hover:bg-[#f6f7fa] disabled:opacity-40 disabled:cursor-not-allowed transition';
const BTN_PRI = 'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md bg-[#1c4ed8] text-[13px] font-medium text-white whitespace-nowrap hover:brightness-110 disabled:opacity-40 transition';
const SELECT = 'h-8 text-[13px] font-medium text-[#0d1524] bg-white border border-[#cbd3df] rounded-md px-2.5 pr-7 focus:outline-none focus:border-[#1c4ed8] cursor-pointer hover:bg-[#f6f7fa] transition';
const WEEK = 7 * 24 * 60 * 60 * 1000;

const RISK = {
  high: { label: 'High', color: '#bd2f2b' },
  medium: { label: 'Medium', color: '#c9a82b' },
  low: { label: 'Low', color: '#2f9e6b' },
};

function pad2(n) {
  return String(n).padStart(2, '0');
}

function dayKey(value) {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-CA');
}

function lastSevenKeys() {
  const now = Date.now();
  return [6, 5, 4, 3, 2, 1, 0].map(i => new Date(now - i * 86400000).toLocaleDateString('en-CA'));
}

function Spark({ values }) {
  const w = 64;
  const h = 22;
  const max = Math.max(...values, 1);
  const pts = values.map((v, i) => [(i * (w - 6)) / (values.length - 1) + 3, h - 3 - (v / max) * (h - 7)]);
  const line = pts.map(p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const last = pts[pts.length - 1];
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} fill="none" aria-hidden="true" className="flex-none">
      <polygon points={`3,${h - 1} ${line} ${w - 3},${h - 1}`} fill="#1c4ed8" opacity=".12" />
      <polyline points={line} stroke="#1c4ed8" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r="2.3" fill="#1c4ed8" />
    </svg>
  );
}

function HighPill({ count }) {
  if (!count) return <span className="text-[13px] text-[#76829a]">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs leading-none font-semibold px-2 py-1.5 rounded text-[#bd2f2b] bg-[#fae8e7]">
      <i className="w-1.5 h-1.5 rounded-full bg-current" />{count} high
    </span>
  );
}

function CompaniesView({
  companies = [],
  records = [],
  initialTab = 'monitored',
  watchlist,
  toggleWatchlist,
  updatedWatchlistIds,
  setView,
  onOpenRecord,
  onAddCompany,
  onViewStories,
  onCompaniesChange,
  onRefreshRecords,
}) {
  const [tab, setTab] = useState(initialTab);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('stories');
  const [filter, setFilter] = useState('all');
  const [selectedName, setSelectedName] = useState(null);
  const [panelOpen, setPanelOpen] = useState(true);
  const [collect, setCollect] = useState({});   // name -> 'queued' | 'running' | 'done' | 'failed'
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [notice, setNotice] = useState('');
  const [configuring, setConfiguring] = useState(null);
  const collectRef = useRef(collect);
  collectRef.current = collect;

  const savedCount = useMemo(
    () => records.filter(r => isWatchlisted(r, watchlist)).length,
    [records, watchlist],
  );

  const stats = useMemo(() => {
    const keys = lastSevenKeys();
    const now = Date.now();
    const byName = new Map();
    companies.forEach(c => byName.set(c.name, {
      name: c.name, total: 0, high: 0, medium: 0, low: 0, week: 0, latest: 0,
      days: keys.map(() => 0), categories: {}, stories: [],
    }));
    records.forEach(record => {
      if (record.entity_type === 'person') return;
      const entry = byName.get(record.company);
      if (!entry) return;
      const time = record.published_at ? new Date(record.published_at).getTime() : 0;
      entry.total += 1;
      const level = getRiskLevel(record.severity);
      if (level in RISK) entry[level] += 1;
      if (time && now - time <= WEEK) entry.week += 1;
      entry.latest = Math.max(entry.latest, time);
      const idx = keys.indexOf(dayKey(record.published_at));
      if (idx >= 0) entry.days[idx] += 1;
      (record.categories || []).forEach(cat => { entry.categories[cat] = (entry.categories[cat] || 0) + 1; });
      entry.stories.push(record);
    });
    byName.forEach(entry => {
      entry.stories.sort((a, b) => new Date(b.published_at || 0) - new Date(a.published_at || 0));
    });
    return [...byName.values()];
  }, [companies, records]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = stats.filter(c => !q || c.name.toLowerCase().includes(q));
    if (filter === 'risk') list = list.filter(c => c.high > 0);
    if (filter === 'quiet') list = list.filter(c => c.week === 0);
    const by = {
      stories: (a, b) => b.total - a.total || a.name.localeCompare(b.name),
      risk: (a, b) => b.high - a.high || b.total - a.total || a.name.localeCompare(b.name),
      latest: (a, b) => b.latest - a.latest || a.name.localeCompare(b.name),
      name: (a, b) => a.name.localeCompare(b.name),
    }[sort];
    return [...list].sort(by);
  }, [stats, search, filter, sort]);

  const selected = useMemo(
    () => visible.find(c => c.name === selectedName) || visible[0] || null,
    [visible, selectedName],
  );

  const totals = useMemo(() => ({
    stories: stats.reduce((n, c) => n + c.total, 0),
    high: stats.reduce((n, c) => n + c.high, 0),
    quiet: stats.filter(c => c.week === 0).length,
  }), [stats]);

  const flash = useCallback((message) => {
    setNotice(message);
    setTimeout(() => setNotice(''), 3500);
  }, []);

  // Poll the collection queue for every company we asked to refresh.
  useEffect(() => {
    const pending = Object.entries(collect).filter(([, s]) => s === 'queued' || s === 'running');
    if (pending.length === 0) return undefined;
    const timer = setInterval(async () => {
      for (const [name] of Object.entries(collectRef.current).filter(([, s]) => s === 'queued' || s === 'running')) {
        try {
          const status = await apiFetch(`/companies/${encodeURIComponent(name)}/fetch-status`);
          if (status.status === 'idle') {
            setCollect(prev => ({ ...prev, [name]: status.failed ? 'failed' : 'done' }));
            onRefreshRecords?.();
          } else {
            setCollect(prev => ({ ...prev, [name]: status.status }));
          }
        } catch {
          setCollect(prev => ({ ...prev, [name]: 'failed' }));
        }
      }
    }, 4000);
    return () => clearInterval(timer);
  }, [collect, onRefreshRecords]);

  const refreshCompany = async (name) => {
    setCollect(prev => ({ ...prev, [name]: 'queued' }));
    try {
      await apiFetch(`/companies/${encodeURIComponent(name)}/fetch`, { method: 'POST' });
    } catch (err) {
      setCollect(prev => ({ ...prev, [name]: 'failed' }));
      flash(`Could not start collection for ${name}: ${err.message}`);
    }
  };

  const removeCompany = async (name) => {
    setRemoving(true);
    try {
      await apiFetch(`/companies/${encodeURIComponent(name)}`, { method: 'DELETE' });
      setConfirmRemove(null);
      setSelectedName(null);
      await onCompaniesChange?.();
      await onRefreshRecords?.();
      flash(`${name} removed`);
    } catch (err) {
      flash(`Could not remove ${name}: ${err.message}`);
    } finally {
      setRemoving(false);
    }
  };

  const dirty = Boolean(search || filter !== 'all');
  const showPanel = panelOpen && selected;

  if (configuring) {
    return (
      <CompanyConfig
        company={{ name: configuring }}
        onBack={() => setConfiguring(null)}
        onFetch={refreshCompany}
      />
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl leading-tight font-semibold tracking-tight text-[#0d1524]">
            {companies.length} {companies.length === 1 ? 'company' : 'companies'} monitored
          </h1>
          <p className="mt-1 text-sm text-[#45516a]">
            {totals.stories} {totals.stories === 1 ? 'story' : 'stories'} collected · {totals.high} high risk
            {totals.quiet > 0 ? ` · ${totals.quiet} quiet this week` : ''}
          </p>
        </div>
        <button type="button" className={BTN_PRI} onClick={onAddCompany}>
          <Plus size={14} />Add company
        </button>
      </div>

      {/* Tabs */}
      <div role="tablist" className="flex items-end gap-[22px] border-b border-[#e1e5ec] overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {[['monitored', 'Monitored companies', companies.length], ['saved', 'Saved stories', savedCount]].map(([key, label, count]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-[7px] py-2.5 -mb-px text-[13px] whitespace-nowrap border-b-2 ${
              tab === key ? 'text-[#1c4ed8] border-[#1c4ed8] font-medium' : 'text-[#45516a] border-transparent hover:text-[#0d1524]'
            }`}
          >
            {label}
            <em className="not-italic text-[11px] font-medium bg-[#f6f7fa] border border-[#e1e5ec] rounded px-[5px] py-px text-[#45516a]">{count}</em>
          </button>
        ))}
      </div>

      {tab === 'saved' ? (
        <div className="mt-4">
          <WatchlistView
            records={records}
            watchlist={watchlist}
            toggleWatchlist={toggleWatchlist}
            setView={setView}
            onOpenRecord={onOpenRecord}
            updatedWatchlistIds={updatedWatchlistIds}
          />
        </div>
      ) : (
        <>
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2.5 mt-4">
            <div className="relative flex-1 min-w-[220px] max-w-[420px]">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#76829a] pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search monitored companies"
                className="h-8 w-full text-[13px] text-[#0d1524] bg-white border border-[#cbd3df] rounded-md pl-9 pr-8 focus:outline-none focus:border-[#1c4ed8] placeholder:text-[#76829a]"
              />
              {search && (
                <button type="button" onClick={() => setSearch('')} aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#76829a] hover:text-[#0d1524]">
                  <X size={14} />
                </button>
              )}
            </div>
            <select value={filter} onChange={e => setFilter(e.target.value)} className={SELECT} aria-label="Filter companies">
              {FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <select value={sort} onChange={e => setSort(e.target.value)} className={SELECT} aria-label="Sort companies">
              {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            {dirty && (
              <button type="button" onClick={() => { setSearch(''); setFilter('all'); }}
                className="text-[12.5px] text-[#1c4ed8] hover:underline">Reset</button>
            )}
            <span className="flex-1" />
            {notice && <span className="text-[12.5px] text-[#45516a]">{notice}</span>}
            <button
              type="button"
              onClick={() => setPanelOpen(o => !o)}
              aria-pressed={panelOpen}
              aria-label={panelOpen ? 'Hide company panel' : 'Show company panel'}
              title={panelOpen ? 'Hide company panel' : 'Show company panel'}
              className={`hidden xl:grid w-8 h-8 place-items-center rounded-md border transition ${
                panelOpen ? 'border-[#1c4ed8] bg-[#e7edfc] text-[#1c4ed8]' : 'border-[#cbd3df] bg-white text-[#45516a] hover:bg-[#f6f7fa]'
              }`}
            >
              <Building2 size={15} />
            </button>
          </div>

          <div className={`grid gap-4 mt-3.5 items-start ${showPanel ? 'xl:grid-cols-[minmax(0,1fr)_340px]' : 'grid-cols-1'}`}>
            {/* List */}
            <div className={`${CARD} overflow-x-auto min-w-0`}>
              <div className="grid grid-cols-[minmax(0,1.2fr)_64px_84px_80px_minmax(0,1.5fr)_28px] gap-3 px-3.5 py-2.5 pl-[17px] bg-[#f6f7fa] rounded-t-lg text-xs font-medium text-[#5b6780] min-w-[640px]">
                <span>Company</span>
                <span>Stories</span>
                <span>High risk</span>
                <span>7 days</span>
                <span>Latest story</span>
                <span />
              </div>

              {companies.length === 0 ? (
                <div className="text-center py-14 px-6 border-t border-[#e1e5ec]">
                  <Building2 size={26} className="mx-auto text-[#76829a]" />
                  <p className="mt-2 text-sm font-medium text-[#0d1524]">No companies monitored yet</p>
                  <p className="mt-1 text-[13px] text-[#76829a]">Add a company and collection starts in the background.</p>
                  <button type="button" className={`${BTN_PRI} mt-3`} onClick={onAddCompany}><Plus size={14} />Add company</button>
                </div>
              ) : visible.length === 0 ? (
                <div className="text-center py-12 px-6 border-t border-[#e1e5ec]">
                  <p className="text-sm font-medium text-[#0d1524]">No companies match</p>
                  <button type="button" className={`${BTN} mt-3`} onClick={() => { setSearch(''); setFilter('all'); }}>Reset filters</button>
                </div>
              ) : visible.map(company => {
                const isSel = selected && selected.name === company.name;
                const state = collect[company.name];
                const busy = state === 'queued' || state === 'running';
                const top = company.stories[0];
                return (
                  <div
                    key={company.name}
                    role="button"
                    tabIndex={0}
                    onClick={() => { setSelectedName(company.name); setPanelOpen(true); }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedName(company.name); setPanelOpen(true); }
                    }}
                    className={`grid grid-cols-[minmax(0,1.2fr)_64px_84px_80px_minmax(0,1.5fr)_28px] gap-3 items-center px-3.5 py-3 border-t border-[#e1e5ec] border-l-[3px] cursor-pointer min-w-[640px] hover:bg-[#f6f7fa] ${
                      isSel ? '!bg-[#e7edfc] border-l-[#1c4ed8]' : company.high ? 'border-l-[#bd2f2b]' : 'border-l-transparent'
                    }`}
                  >
                    <span className="flex items-center gap-2.5 min-w-0">
                      <CompanyLogo name={company.name} size="md" />
                      <span className="min-w-0">
                        <b className="block truncate text-sm font-semibold text-[#0d1524]">{company.name}</b>
                        <small className="text-[11.5px] text-[#76829a]">
                          {busy ? 'Collecting…' : company.latest ? `Updated ${relativeTime(new Date(company.latest).toISOString())}` : 'No stories yet'}
                        </small>
                      </span>
                    </span>
                    <span className={`text-sm ${company.total ? 'font-semibold text-[#0d1524]' : 'text-[#76829a]'}`}>{company.total}</span>
                    <HighPill count={company.high} />
                    <Spark values={company.days} />
                    <span className="min-w-0 text-[13px] text-[#45516a] line-clamp-2">
                      {top ? displayHeadline(top) : '—'}
                    </span>
                    <button type="button" title={`${company.name} settings`} aria-label={`${company.name} settings`}
                      onClick={(e) => { e.stopPropagation(); setConfiguring(company.name); }}
                      className="w-7 h-7 -mr-1 grid place-items-center rounded-md text-[#76829a] hover:text-[#1c4ed8] hover:bg-[#e7edfc] transition">
                      <SlidersHorizontal size={15} />
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Detail panel */}
            {showPanel && (() => {
              const state = collect[selected.name];
              const busy = state === 'queued' || state === 'running';
              const maxCat = Math.max(1, ...Object.values(selected.categories));
              return (
                <aside className={`${CARD} p-[18px] xl:sticky xl:top-[70px]`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-[#0d1524]">Selected company</span>
                    <span className="flex items-center gap-1.5">
                      <button type="button" onClick={() => setConfiguring(selected.name)}
                        className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-[#cbd3df] bg-white text-[13px] font-medium text-[#0d1524] hover:bg-[#f6f7fa] transition">
                        <SlidersHorizontal size={13} />Settings
                      </button>
                      <button type="button" onClick={() => setPanelOpen(false)} aria-label="Close"
                        className="w-[26px] h-[26px] grid place-items-center rounded-md text-[#45516a] hover:bg-[#f6f7fa]">
                        <X size={14} />
                      </button>
                    </span>
                  </div>

                  <div className="flex items-center gap-3 mt-3.5">
                    <CompanyLogo name={selected.name} size="lg" />
                    <div className="min-w-0">
                      <h2 className="text-[17px] leading-tight font-semibold text-[#0d1524] truncate">{selected.name}</h2>
                      <span className="text-xs text-[#76829a]">
                        {busy ? 'Collecting…' : selected.latest ? `Updated ${relativeTime(new Date(selected.latest).toISOString())}` : 'No stories yet'}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mt-4">
                    {[['Stories', selected.total, ''], ['High risk', selected.high, selected.high ? 'text-[#bd2f2b]' : ''], ['This week', selected.week, '']].map(([label, n, tone]) => (
                      <div key={label} className="rounded-md bg-[#f6f7fa] border border-[#e1e5ec] px-3 py-2.5">
                        <b className={`block text-xl font-semibold tracking-[-0.02em] ${tone}`}>{pad2(n)}</b>
                        <small className="text-xs text-[#5b6780]">{label}</small>
                      </div>
                    ))}
                  </div>

                  {selected.total === 0 ? (
                    <div className="mt-4 rounded-md border border-dashed border-[#cbd3df] px-4 py-6 text-center">
                      <p className="text-sm font-medium text-[#0d1524]">No stories collected yet</p>
                      <p className="mt-1 text-[12.5px] text-[#76829a]">Run a collection to pull the latest reporting for {selected.name}.</p>
                      <button type="button" className={`${BTN_PRI} mt-3`} onClick={() => refreshCompany(selected.name)} disabled={busy}>
                        {busy ? <><Loader2 size={14} className="animate-spin" />Collecting…</> : <><RefreshCw size={14} />Collect now</>}
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="mt-5">
                        <PanelTitle>Risk mix</PanelTitle>
                        <div className="flex h-2.5 gap-0.5 rounded overflow-hidden mt-3">
                          {['high', 'medium', 'low'].filter(k => selected[k]).map(k => (
                            <span key={k} style={{ flex: selected[k], background: RISK[k].color }} className="min-w-[6px]" title={`${RISK[k].label}: ${selected[k]}`} />
                          ))}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2.5 text-[13px] text-[#45516a]">
                          {['high', 'medium', 'low'].map(k => (
                            <span key={k} className="flex items-center gap-1.5">
                              <i className="w-2 h-2 rounded-full" style={{ background: RISK[k].color }} />
                              {RISK[k].label} <b className="font-semibold text-[#0d1524]">{selected[k]}</b>
                            </span>
                          ))}
                        </div>
                      </div>

                      {Object.keys(selected.categories).length > 0 && (
                        <div className="mt-5">
                          <PanelTitle>Top categories</PanelTitle>
                          <div className="mt-2.5">
                            {Object.entries(selected.categories).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([cat, n]) => (
                              <div key={cat} className="grid grid-cols-[84px_1fr_28px] items-center gap-3 py-1.5 text-[13px]">
                                <span className="capitalize truncate text-[#0d1524]">{cat}</span>
                                <i className="block h-1.5 rounded bg-[#f6f7fa] overflow-hidden">
                                  <s className="block h-full rounded bg-[#1c4ed8] no-underline" style={{ width: `${(n / maxCat) * 100}%` }} />
                                </i>
                                <em className="not-italic text-right font-semibold text-[#0d1524]">{n}</em>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="mt-5">
                        <PanelTitle>Latest stories</PanelTitle>
                        <div className="mt-2.5 rounded-md border border-[#e1e5ec] overflow-hidden">
                          {selected.stories.slice(0, 3).map(story => {
                            const level = getRiskLevel(story.severity);
                            return (
                              <button key={story.id || story.url} type="button" onClick={() => onOpenRecord(story)}
                                className="flex w-full items-start gap-2.5 text-left px-3 py-2.5 border-t border-[#e1e5ec] first:border-t-0 hover:bg-[#f6f7fa] transition">
                                <i className="w-2 h-2 rounded-full mt-1.5 flex-none" style={{ background: RISK[level]?.color || '#94a3b8' }} title={RISK[level]?.label} />
                                <span className="min-w-0">
                                  <span className="block text-[13px] font-medium leading-snug line-clamp-2 text-[#0d1524]">{displayHeadline(story)}</span>
                                  <small className="text-xs text-[#76829a]">{story.published_at ? formatShortDate(story.published_at) : 'Date unknown'}</small>
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <button type="button" className={`${BTN_PRI} w-full mt-5`} onClick={() => onViewStories(selected.name)}>
                        View all {selected.total} stories<ChevronRight size={14} />
                      </button>
                      <div className="flex gap-2 mt-2">
                        <button type="button" className={`${BTN} flex-1`} onClick={() => refreshCompany(selected.name)} disabled={busy}>
                          {busy ? <><Loader2 size={14} className="animate-spin" />Collecting…</>
                            : state === 'done' ? <><Check size={14} className="text-[#117049]" />Updated</>
                              : state === 'failed' ? <><AlertCircle size={14} className="text-[#bd2f2b]" />Retry</>
                                : <><RefreshCw size={14} />Refresh</>}
                        </button>
                      </div>
                    </>
                  )}

                  <div className="mt-5 pt-3.5 border-t border-[#e1e5ec]">
                    {confirmRemove === selected.name ? (
                      <div className="rounded-md bg-[#fae8e7] p-3 text-[12.5px] text-[#0d1524]">
                        Remove <b>{selected.name}</b> and delete its {selected.total} collected {selected.total === 1 ? 'story' : 'stories'}? This cannot be undone.
                        <div className="flex gap-2 mt-2.5">
                          <button type="button" className={`${BTN} flex-1`} onClick={() => setConfirmRemove(null)} disabled={removing}>Cancel</button>
                          <button type="button" onClick={() => removeCompany(selected.name)} disabled={removing}
                            className="inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md bg-[#bd2f2b] text-[13px] font-medium text-white flex-1 hover:brightness-110 disabled:opacity-50">
                            {removing ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button type="button" onClick={() => setConfirmRemove(selected.name)}
                        className="inline-flex items-center gap-1.5 text-[12.5px] text-[#bd2f2b] hover:underline">
                        <Trash2 size={13} />Remove company
                      </button>
                    )}
                  </div>
                </aside>
              );
            })()}
          </div>
        </>
      )}
    </div>
  );
}

function PanelTitle({ children }) {
  return (
    <h3 className="flex items-center gap-2 text-sm font-semibold text-[#0d1524]">
      <span className="w-1 h-4 rounded-full bg-[#1c4ed8]" />
      {children}
    </h3>
  );
}

export default CompaniesView;
